import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Brain, MessageSquare, Shield, Sparkles, Target, Zap, Award, CheckCircle, TrendingUp } from 'lucide-react';
import { GlowCard } from '../components/GlowCard';
import { founderContent } from '../data/founderContent';

const methodologySteps = [
  {
    number: '01',
    title: 'Nöro-UX Tasarım',
    description: 'Göz takibi prensipleri, bilişsel yük optimizasyonu ve duygusal tetikleyici haritalama ile kullanıcı deneyimini bilinçaltı seviyesinde optimize ediyoruz.',
    icon: Eye,
    features: [
      'F-Pattern ve Z-Pattern göz takibi optimizasyonu',
      'Bilişsel yük azaltma teknikleri',
      'Duygusal tetikleyici noktalarının stratejik yerleşimi',
      'Dikkat çeken görsel hiyerarşi tasarımı',
    ],
  },
  {
    number: '02',
    title: 'Mesaj Mimarisi',
    description: 'Hipnotik dil kalıpları ve bilinçaltı ikna çerçeveleri ile markanızın mesajını zihinlere nakşediyoruz.',
    icon: MessageSquare,
    features: [
      'Hipnotik dil kalıpları entegrasyonu',
      'Bilinçaltı ikna framework\'leri',
      'Hikaye anlatımı psikolojisi',
      'Duygusal bağ kurma teknikleri',
    ],
  },
  {
    number: '03',
    title: 'Etik İkna',
    description: 'Uzun vadeli güven inşası ve otantik marka sesi ile şeffaf ikna stratejileri uyguluyoruz.',
    icon: Shield,
    features: [
      'Şeffaflık odaklı ikna stratejileri',
      'Uzun vadeli güven inşası',
      'Otantik marka sesi geliştirme',
      'Etik pazarlama standartları',
    ],
  },
];

const principles = [
  {
    title: 'Bilinçaltı Tetikleyiciler',
    description: 'İnsan beyninin karar verme süreçlerini anlayarak, doğru anlarda doğru mesajları iletiyoruz.',
    icon: Brain,
  },
  {
    title: 'Duygusal Bağ',
    description: 'Mantıksal argümanların ötesinde, duygusal rezonans yaratan deneyimler tasarlıyoruz.',
    icon: Sparkles,
  },
  {
    title: 'Hedef Odaklı Tasarım',
    description: 'Her tasarım kararı, belirlenen dönüşüm hedeflerine hizmet eder.',
    icon: Target,
  },
  {
    title: 'Hız ve Verimlilik',
    description: 'Kullanıcının hedefine en kısa yoldan ulaşmasını sağlayan akışlar tasarlıyoruz.',
    icon: Zap,
  },
];

export default function MethodologyPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32" data-testid="methodology-page">
      {/* Hero Section with Background Image */}
      <section className="section-padding relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Taner Torun Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/taner-torun.png" 
            alt="" 
            className="absolute right-[-8%] top-1/2 -translate-y-1/2 h-[80%] w-auto object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/50" />
          <div className="absolute inset-0 bg-[#0A0A0A]/30" />
        </div>
        
        <div className="container-wide relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Sol: Taner Torun Bilgileri + Quote */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-xl"
            >
              <span className="text-xs font-mono text-violet-400 tracking-widest uppercase mb-4 block">
                Zihin Mimarı
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                {founderContent.name}
              </h1>
              <p className="text-xl text-violet-400 font-medium mb-6">
                {founderContent.title}
              </p>
              
              {/* Uzmanlık Alanları */}
              <div className="flex flex-wrap gap-2 mb-8">
                {founderContent.expertise.map((item, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 text-xs font-medium bg-zinc-800/50 text-zinc-300 rounded-full border border-zinc-700/50"
                  >
                    {item}
                  </span>
                ))}
              </div>
              
              {/* Tagline */}
              <p className="text-lg text-zinc-400 leading-relaxed mb-8">
                {founderContent.microCopy.tagline}
              </p>
              
              {/* Kanıt Noktaları */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {founderContent.proofPoints.map((point, index) => (
                  <div key={index} className="text-center p-4 bg-zinc-900/50 rounded-xl border border-zinc-800/50">
                    <div className="text-2xl font-bold gradient-text mb-1">{point.value}</div>
                    <div className="text-xs text-zinc-500">{point.label}</div>
                  </div>
                ))}
              </div>
              
              {/* Quote Card - Moved here */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <div className="methodology-card">
                  <div className="relative z-10">
                    <svg className="w-10 h-10 text-violet-500/30 mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                    <blockquote className="text-lg md:text-xl font-medium text-white leading-relaxed mb-5">
                      "{founderContent.microCopy.quote}"
                    </blockquote>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        TT
                      </div>
                      <div>
                        <p className="text-white font-semibold">{founderContent.name}</p>
                        <p className="text-zinc-500 text-sm">{founderContent.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Sağ: Boş - Arka plan görseli için */}
            <div className="hidden lg:block" />
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase mb-4 block">
              20 Yıllık Deneyim
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Taner Torun Metodolojisi
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {founderContent.longFormBio.paragraphs.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlowCard className="p-8 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center">
                      <span className="text-violet-400 font-bold">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{section.title}</h3>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">{section.content}</p>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-900/20 via-[#121212] to-pink-900/20 border border-violet-500/20 p-12 text-center">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50" />
              <div className="relative z-10">
                <Award className="w-16 h-16 text-violet-400 mx-auto mb-6" />
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {founderContent.microCopy.valueSentence}
                </h3>
                <p className="text-zinc-400 max-w-2xl mx-auto">
                  {founderContent.shortTeaser}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Neuro-Marketing Principles */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-pink-400 tracking-widest uppercase mb-4 block">
              Medyatik Farkı
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Nöro-Pazarlama Prensipleri
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {founderContent.neuroMarketingPrinciples.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <GlowCard className="p-8 h-full text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-6">
                    {index === 0 && <Brain className="w-8 h-8 text-violet-400" />}
                    {index === 1 && <TrendingUp className="w-8 h-8 text-emerald-400" />}
                    {index === 2 && <CheckCircle className="w-8 h-8 text-pink-400" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{principle.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{principle.description}</p>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Steps */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase mb-4 block">
              3 Temel Sütun
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Metodoloji Detayları
            </h2>
          </motion.div>

          <div className="space-y-16">
            {methodologySteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-mono text-6xl font-bold text-zinc-800">{step.number}</span>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-violet-400" />
                    </div>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{step.title}</h3>
                  <p className="text-zinc-400 text-lg leading-relaxed mb-8">{step.description}</p>
                  
                  <ul className="space-y-4">
                    {step.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-emerald-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        </div>
                        <span className="text-zinc-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <GlowCard className="p-8 h-full">
                    <div className="aspect-video bg-gradient-to-br from-violet-500/10 to-pink-500/10 rounded-xl flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-violet-400/50" />
                    </div>
                  </GlowCard>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Pillars */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-violet-400 tracking-widest uppercase mb-4 block">
              Hizmet Sütunları
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Büyüme Sistemlerimiz
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {founderContent.servicePillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="relative group h-full">
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 to-pink-500/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100" />
                  <div className="relative bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-8 h-full hover:border-violet-500/30 transition-colors">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center mb-6">
                      <span className="text-2xl font-bold gradient-text">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">{pillar.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{pillar.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-pink-400 tracking-widest uppercase mb-4 block">
              Temel Prensipler
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Tasarım Felsefemiz
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlowCard className="p-6 h-full text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 flex items-center justify-center mx-auto mb-6">
                    <principle.icon className="w-7 h-7 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{principle.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{principle.description}</p>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Messages */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Neden Medyatik?
              </h2>
              <p className="text-zinc-400 text-lg">
                20 yıllık deneyim ve sürekli yenilik ile dijital büyümenizi tasarlıyoruz.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {founderContent.keyMessages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-zinc-800/50 rounded-xl hover:border-violet-500/30 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-400/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                  <span className="text-zinc-300">{message}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="methodology-card text-center max-w-3xl mx-auto"
          >
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Bu Metodolojiyi
                <br />
                <span className="gradient-text">Projenizde Kullanın</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-10">
                Taner Torun Metodolojisi ile markanızı sadece görünür değil, 
                hissedilir ve tercih edilir yapın.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/iletisim" 
                  className="btn-gradient flex items-center gap-2"
                  data-testid="methodology-cta-primary"
                >
                  Büyüme Analizi Talep Et
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/portfolyo" 
                  className="btn-glow"
                  data-testid="methodology-cta-secondary"
                >
                  Uygulama Örnekleri
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
